import { useState } from 'react';
import { Container, Typography, Card, CardContent, Grid, Stack, TextField, MenuItem, Box, Avatar, Dialog } from '@mui/material';
import { fetchMyCollaborations, today, uploadImage } from '../../lib/api';
import { track } from '../../lib/analytics';
import { uid } from '../../lib/format';
import { navigate } from '../../lib/navigation';
import { useOwnedSection } from '../../lib/useOwnedSection';
import { defaultCollab } from '../../lib/collaboration';
import Button from '../../components/Button';
import FormDisclosure from '../../components/FormDisclosure';
import PageHeader from '../../components/PageHeader';

function NewCollaborationPage({ identityActions }) {
  const { loading, setItems: setCollaborations } = useOwnedSection(
    fetchMyCollaborations,
    '/v1/profiles/me/collaborations',
    (next) => ({ collaborations: next })
  );
  const [form, setForm] = useState(defaultCollab());
  const [uploading, setUploading] = useState(false);
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);
  function updateDeliverable(id, patch) {
    setForm({ ...form, deliverables: form.deliverables.map((item) => (item.id === id ? { ...item, ...patch } : item)) });
  }

  async function handleLogoUpload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setForm((current) => ({ ...current, brandLogoUrl: url }));
    } catch (error) {
      identityActions.showErrorToast(error.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PageHeader title="Add brand work" description="Capture just enough campaign context to make the creator profile useful to brands." />
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Brand name"
                value={form.brand}
                onChange={(event) => setForm({ ...form, brand: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Campaign name"
                value={form.campaign}
                onChange={(event) => setForm({ ...form, campaign: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Collaboration type"
                value={form.type}
                onChange={(event) => setForm({ ...form, type: event.target.value })}
              >
                {[
                  'Paid campaign',
                  'Gifted campaign',
                  'Affiliate',
                  'UGC',
                  'Ambassador',
                  'Event',
                  'Product seeding',
                  'Freelance creative work',
                  'Other',
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Campaign status"
                value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value })}
              >
                {['Planned', 'In progress', 'Completed', 'Cancelled'].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <FormDisclosure label="More details">
                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      onClick={() => form.brandLogoUrl && setLogoPreviewOpen(true)}
                      sx={{ cursor: form.brandLogoUrl ? 'pointer' : 'default', flexShrink: 0 }}
                    >
                      <Avatar src={form.brandLogoUrl} variant="rounded" sx={{ width: 64, height: 64 }} />
                    </Box>
                    <Button variant="secondary" component="label" disabled={uploading}>
                      {uploading ? 'Uploading…' : 'Upload brand logo'}
                      <input hidden type="file" accept="image/*" onChange={(event) => handleLogoUpload(event.target.files?.[0])} />
                    </Button>
                  </Box>
                </Grid>
                <Dialog open={logoPreviewOpen} onClose={() => setLogoPreviewOpen(false)} maxWidth="sm">
                  <Box component="img" src={form.brandLogoUrl} alt="Brand logo preview" sx={{ width: '100%', display: 'block' }} />
                </Dialog>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Campaign description"
                    placeholder="Created three short-form videos for the brand's product launch campaign."
                    helperText="Summarise the campaign and what you contributed."
                    value={form.description}
                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={form.startDate}
                    onChange={(event) => setForm({ ...form, startDate: event.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={form.endDate}
                    onChange={(event) => setForm({ ...form, endDate: event.target.value })}
                  />
                </Grid>
              </FormDisclosure>
            </Grid>
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Content links
              </Typography>
              <Stack spacing={1.5}>
                {form.deliverables.map((item) => (
                  <Grid container spacing={1} key={item.id}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Platform"
                        value={item.platform}
                        onChange={(event) => updateDeliverable(item.id, { platform: event.target.value })}
                      >
                        {['TikTok', 'Douyin', 'Instagram', 'YouTube', 'Other'].map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Content URL"
                        value={item.contentUrl}
                        onChange={(event) => updateDeliverable(item.id, { contentUrl: event.target.value })}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Stack>
              <Button
                variant="secondary"
                sx={{ mt: 1.5 }}
                onClick={() =>
                  setForm({
                    ...form,
                    deliverables: [
                      ...form.deliverables,
                      {
                        id: uid('del'),
                        platform: 'Instagram',
                        dueDate: today,
                        status: 'Complete',
                        contentUrl: '',
                      },
                    ],
                  })
                }
              >
                Add more links
              </Button>
            </Grid>
            <Grid size={12}>
              <Button
                fullWidth
                disabled={loading}
                onClick={async () => {
                  if (!form.brand || !form.campaign) return identityActions.showErrorToast('Brand and campaign are required');
                  track('collaboration_added', { entityType: 'collaboration', label: form.brand });
                  await setCollaborations((current) => [{ ...form, id: uid('col'), testimonial: '' }, ...current]);
                  identityActions.showToast('Collaboration created');
                  navigate('/dashboard/collaborations');
                }}
              >
                Save collaboration
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
}

export default NewCollaborationPage;
